import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { formatDistanceToNow } from 'date-fns';
import { 
  Loader2, 
  FileText, 
  AlertCircle, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  Copy,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// Format elapsed time in a human-readable format (e.g., "1.2s", "150ms", "2m30.5s")
const formatElapsed = (ms: number): string => {
  if (ms < 1000) {
    return `${ms}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)}s`;
  } else {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(1);
    return `${minutes}m${seconds}s`;
  }
};

// Format timestamp to show just the time (HH:MM:SS)
const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  } catch {
    return '';
  }
};

// Calculate elapsed time from execution start
const calculateElapsed = (logTimestamp: string, executionStart: string): number => {
  try {
    const logTime = new Date(logTimestamp).getTime();
    const startTime = new Date(executionStart).getTime();
    return Math.max(0, logTime - startTime);
  } catch {
    return 0;
  }
};

// ANSI color code mappings
const ANSI_COLORS: Record<number, string> = {
  30: 'text-gray-900 dark:text-gray-100',     // Black
  31: 'text-red-600 dark:text-red-400',       // Red
  32: 'text-green-600 dark:text-green-400',   // Green
  33: 'text-yellow-600 dark:text-yellow-400', // Yellow
  34: 'text-blue-600 dark:text-blue-400',     // Blue
  35: 'text-purple-600 dark:text-purple-400', // Magenta
  36: 'text-cyan-600 dark:text-cyan-400',     // Cyan
  37: 'text-gray-100 dark:text-gray-900',     // White
  90: 'text-gray-500',                         // Bright Black (Gray)
  91: 'text-red-500',                          // Bright Red
  92: 'text-green-500',                        // Bright Green
  93: 'text-yellow-500',                       // Bright Yellow
  94: 'text-blue-500',                         // Bright Blue
  95: 'text-purple-500',                       // Bright Magenta
  96: 'text-cyan-500',                         // Bright Cyan
  97: 'text-white',                            // Bright White
};

const ANSI_BG_COLORS: Record<number, string> = {
  40: 'bg-gray-900',     // Black
  41: 'bg-red-600',      // Red
  42: 'bg-green-600',    // Green
  43: 'bg-yellow-600',   // Yellow
  44: 'bg-blue-600',     // Blue
  45: 'bg-purple-600',   // Magenta
  46: 'bg-cyan-600',     // Cyan
  47: 'bg-gray-100',     // White
  100: 'bg-gray-500',    // Bright Black
  101: 'bg-red-500',     // Bright Red
  102: 'bg-green-500',   // Bright Green
  103: 'bg-yellow-500',  // Bright Yellow
  104: 'bg-blue-500',    // Bright Blue
  105: 'bg-purple-500',  // Bright Magenta
  106: 'bg-cyan-500',    // Bright Cyan
  107: 'bg-white',       // Bright White
};

interface AnsiSegment {
  text: string;
  classes: string[];
}

// Parse ANSI escape codes and convert to styled segments
const parseAnsi = (text: string): AnsiSegment[] => {
  const segments: AnsiSegment[] = [];
  // Match ANSI escape sequences: ESC[ followed by numbers separated by ; and ending with m
  const ansiRegex = /\x1b\[([0-9;]*)m/g;
  
  let lastIndex = 0;
  let currentClasses: string[] = [];
  let match;

  while ((match = ansiRegex.exec(text)) !== null) {
    // Add text before this escape sequence
    if (match.index > lastIndex) {
      const segmentText = text.slice(lastIndex, match.index);
      if (segmentText) {
        segments.push({ text: segmentText, classes: [...currentClasses] });
      }
    }

    // Parse the ANSI codes
    const codes = match[1].split(';').map(Number).filter(n => !isNaN(n));
    
    for (const code of codes) {
      if (code === 0) {
        // Reset
        currentClasses = [];
      } else if (code === 1) {
        // Bold
        currentClasses.push('font-bold');
      } else if (code === 2) {
        // Dim
        currentClasses.push('opacity-60');
      } else if (code === 3) {
        // Italic
        currentClasses.push('italic');
      } else if (code === 4) {
        // Underline
        currentClasses.push('underline');
      } else if (code === 9) {
        // Strikethrough
        currentClasses.push('line-through');
      } else if (ANSI_COLORS[code]) {
        // Remove any existing text color classes
        currentClasses = currentClasses.filter(c => !c.startsWith('text-'));
        currentClasses.push(ANSI_COLORS[code]);
      } else if (ANSI_BG_COLORS[code]) {
        // Remove any existing bg color classes
        currentClasses = currentClasses.filter(c => !c.startsWith('bg-'));
        currentClasses.push(ANSI_BG_COLORS[code]);
      }
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), classes: [...currentClasses] });
  }

  // If no segments were created, return the original text
  if (segments.length === 0 && text) {
    segments.push({ text, classes: [] });
  }

  return segments;
};

// Component to render ANSI-styled text
const AnsiText: React.FC<{ text: string }> = ({ text }) => {
  const segments = parseAnsi(text);
  
  if (segments.length === 0) return null;
  if (segments.length === 1 && segments[0].classes.length === 0) {
    return <>{segments[0].text}</>;
  }

  return (
    <>
      {segments.map((segment, i) => (
        segment.classes.length > 0 ? (
          <span key={i} className={segment.classes.join(' ')}>
            {segment.text}
          </span>
        ) : (
          <React.Fragment key={i}>{segment.text}</React.Fragment>
        )
      ))}
    </>
  );
};

// Strip ANSI codes for plain text (used in copy)
const stripAnsi = (text: string): string => {
  return text.replace(/\x1b\[[0-9;]*m/g, '');
};

interface LogEntry {
  level: string;
  message: string;
  data?: any;
  timestamp?: string;
  elapsed?: number; // milliseconds since execution start
}

interface EventData {
  id: string;
  integrationId: string;
  triggerId: string;
  triggerType: string;
  status: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  payload?: any;
}

interface ExecutionLogs {
  id: string;
  eventId: string;
  status: string;
  duration: number;
  memoryUsed?: number;
  logs: LogEntry[];
  error?: string;
  createdAt: string;
}

interface LogViewerProps {
  eventId: string;
  onClose?: () => void;
}

export function LogViewer({ eventId, onClose }: LogViewerProps) {
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [executionLogs, setExecutionLogs] = useState<ExecutionLogs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());

  const fetchLogs = async () => {
    try {
      const response = await deiterateAPI.GET(
        `/v1/integrations/events/${eventId}/logs`,
        {
          backend: 'thalassa'
        }
      );
      
      setEventData(response.event);
      setExecutionLogs(response.executionLogs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchLogs();
  }, [eventId]);

  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'warn':
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'debug':
        return <ChevronRight className="h-4 w-4 text-gray-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'text-red-600 dark:text-red-400';
      case 'warn':
      case 'warning':
        return 'text-amber-600 dark:text-amber-400';
      case 'info':
        return 'text-blue-600 dark:text-blue-400';
      case 'debug':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-emerald-600 dark:text-emerald-400';
    }
  };

  const copyLogs = () => {
    if (!executionLogs?.logs) return;
    
    const executionStart = eventData?.startedAt || eventData?.createdAt || executionLogs.createdAt;
    
    const logText = executionLogs.logs
      .map(log => {
        const elapsed = log.elapsed ?? (log.timestamp ? calculateElapsed(log.timestamp, executionStart) : 0);
        const timeStr = log.timestamp ? formatTimestamp(log.timestamp) : '';
        const prefix = timeStr ? `[${timeStr}] [+${formatElapsed(elapsed)}]` : `[+${formatElapsed(elapsed)}]`;
        // Strip ANSI codes from the message for plain text copy
        const cleanMessage = stripAnsi(log.message);
        return `${prefix} [${log.level.toUpperCase()}] ${cleanMessage}${log.data ? '\n' + JSON.stringify(log.data, null, 2) : ''}`;
      })
      .join('\n\n');
    
    navigator.clipboard.writeText(logText);
  };

  const toggleLogExpansion = (index: number) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLogs(newExpanded);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading logs...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 max-w-full">
      {/* Event Summary */}
      {eventData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <CardTitle className="text-lg flex items-center gap-2 truncate">
                  <FileText className="h-5 w-5 shrink-0" />
                  <span className="truncate">Execution Details</span>
                </CardTitle>
                <CardDescription className="truncate">
                  Event ID: {eventData.id}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant={eventData.status === 'completed' ? 'default' : 'destructive'}>
                  {eventData.status}
                </Badge>
                {onClose && (
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Close
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="min-w-0">
                <span className="font-medium">Integration:</span> 
                <span className="ml-2 truncate block">{eventData.integrationId}</span>
              </div>
              <div className="min-w-0">
                <span className="font-medium">Trigger:</span> 
                <span className="ml-2 truncate block">{eventData.triggerId}</span>
              </div>
              <div className="min-w-0">
                <span className="font-medium">Type:</span> 
                <span className="ml-2 truncate block">{eventData.triggerType}</span>
              </div>
              <div className="min-w-0">
                <span className="font-medium">Created:</span>{' '}
                <span className="ml-2 truncate block">
                  {formatDistanceToNow(new Date(eventData.createdAt), { addSuffix: true })}
                </span>
              </div>
              {eventData.startedAt && (
                <div className="min-w-0">
                  <span className="font-medium">Started:</span>{' '}
                  <span className="ml-2 truncate block">
                    {formatDistanceToNow(new Date(eventData.startedAt), { addSuffix: true })}
                  </span>
                </div>
              )}
              {eventData.completedAt && (
                <div className="min-w-0">
                  <span className="font-medium">Completed:</span>{' '}
                  <span className="ml-2 truncate block">
                    {formatDistanceToNow(new Date(eventData.completedAt), { addSuffix: true })}
                  </span>
                </div>
              )}
            </div>
            
            {executionLogs && (
              <div className="text-sm space-y-2">
                <div>
                  <span className="font-medium">Duration:</span> {executionLogs.duration}ms
                </div>
                <div>
                  <span className="font-medium">Memory Used:</span> {executionLogs.memoryUsed ? `${executionLogs.memoryUsed}MB` : 'N/A'}
                </div>
              </div>
            )}

            {eventData.error && (
              <div className="text-sm">
                <span className="font-medium text-destructive">Error:</span>
                <pre className="mt-1 text-xs bg-destructive/10 text-destructive p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                  {eventData.error}
                </pre>
              </div>
            )}

            {eventData.payload && (
              <div>
                <span className="font-medium text-sm">Payload:</span>
                <div className="mt-1 p-2 bg-muted rounded-md">
                  <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                    {JSON.stringify(eventData.payload, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs */}
      {executionLogs && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Execution Logs</CardTitle>
              <Button variant="outline" size="sm" onClick={copyLogs}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Logs
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {executionLogs.logs && executionLogs.logs.length > 0 ? (
              <ScrollArea className="h-[400px] w-full">
                <div className="p-4 space-y-2">
                  {executionLogs.logs.map((log, index) => {
                    const executionStart = eventData?.startedAt || eventData?.createdAt || executionLogs.createdAt;
                    const elapsed = log.elapsed ?? (log.timestamp ? calculateElapsed(log.timestamp, executionStart) : 0);
                    const timeStr = log.timestamp ? formatTimestamp(log.timestamp) : '';
                    
                    return (
                      <div key={index} className="space-y-1">
                        <div 
                          className="flex items-start gap-2 cursor-pointer hover:bg-muted/50 p-1.5 rounded group"
                          onClick={() => toggleLogExpansion(index)}
                        >
                          {expandedLogs.has(index) ? (
                            <ChevronDown className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          )}
                          
                          {/* Timestamp and elapsed time */}
                          <div className="flex items-center gap-1.5 shrink-0 min-w-[120px]">
                            {timeStr && (
                              <span className="font-mono text-xs text-muted-foreground">
                                {timeStr}
                              </span>
                            )}
                            <span className="font-mono text-xs text-primary/70 bg-primary/10 px-1.5 py-0.5 rounded">
                              +{formatElapsed(elapsed)}
                            </span>
                          </div>
                          
                          {getLevelIcon(log.level)}
                          <span className={`font-mono text-xs ${getLevelColor(log.level)} shrink-0 uppercase`}>
                            {log.level}
                          </span>
                          <span className="text-sm flex-1 min-w-0 break-all font-mono">
                            <AnsiText text={log.message} />
                          </span>
                        </div>
                        
                        {expandedLogs.has(index) && log.data && (
                          <div className="ml-[140px]">
                            <div className="p-2 bg-muted rounded-md">
                              <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-all">
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                        
                        {index < executionLogs.logs.length - 1 && (
                          <Separator className="ml-8" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-center text-muted-foreground py-8">No logs available</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
